import { useQuery } from "@tanstack/react-query";
import { liveProgressApi } from "../api/liveProgress";
import { useLiveProgressMutation } from "../mutations";

export interface ProjectCount {
  total: number;
  completed: number;
  planned: number;
  inProgress: number;
}

export interface PortfolioValue {
  totalBudget: number;
  totalSpent: number;
  totalRemaining: number;
  totalPortfolio: number;
}

export interface RiskCount {
  low: number;
  medium: number;
  high: number;
}

export const useLiveProgress = ({is_construction}: {is_construction?: string}) => {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["live-progress", is_construction],
    queryFn: () => liveProgressApi.fetchLiveProgress(is_construction),
  });

  const mutation = useLiveProgressMutation();

  const projectCount: ProjectCount = {
    total: 0,
    completed: 0,
    planned: 0,
    inProgress: 0,
  };

  if (data?.data?.project_count) {
    data.data.project_count.forEach((item) => {
      const status = item.project_status.toLowerCase();
      const count = parseInt(item.status_count, 10);
      
      if (status === "total") {
        projectCount.total = count;
      } else if (status === "completed") {
        projectCount.completed = count;
      } else if (status === "planned") {
        projectCount.planned = count;
      } else if (status === "in_progress") {
        projectCount.inProgress = count;
      }
    });
  }

  const portfolioValue: PortfolioValue = data?.data?.portfolio_value
    ? {
        totalBudget: parseFloat(data.data.portfolio_value.total_budget),
        totalSpent: parseFloat(data.data.portfolio_value.total_spent),
        totalRemaining: parseFloat(data.data.portfolio_value.total_remaining),
        totalPortfolio: parseInt(data.data.portfolio_value.total_portfolio, 10),
      }
    : {
        totalBudget: 0,
        totalSpent: 0,
        totalRemaining: 0,
        totalPortfolio: 0,
      };

  const riskCount: RiskCount = data?.data?.risk_count
    ? {
        low: data.data.risk_count.low_risk_count,
        medium: data.data.risk_count.medium_risk_count,
        high: data.data.risk_count.high_risk_count,
      }
    : {
        low: 0,
        medium: 0,
        high: 0,
      };

  const avgProgress = data?.data?.percentage?.avg_progress_per
    ? parseFloat(data.data.percentage.avg_progress_per)
    : 0;

  return {
    projectCount,
    portfolioValue,
    riskCount,
    avgProgress,
    isLoading: isLoading || mutation.isPending,
    isError,
    error:
      error instanceof Error
        ? error.message
        : "Failed to fetch live progress data",
    refetch: mutation.mutate,
  };
};

